import type { UseMutationResult, UseQueryResult } from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { paywallSubscriptionsApi } from "@/react/api/paywallSubscriptionsApi";
import type { CommunityMemberSubscription } from "@/react/types/CommunityMemberSubscription";
import { useToast } from "@circle-react-uikit/ToastV2";

const { cancelAndRefundSubscription, refundOptionsSubscription } =
  paywallSubscriptionsApi;

interface RefundOptionsResponse {
  full_refund_applicable: boolean;
  prorated_applicable: boolean;
}

interface CancelAndRefundCallbacks {
  cancelAndRefundSubscription: {
    onSuccess: () => void;
    onError: () => void;
  };
}

interface UsePaywallRefundSubscriptionApiProps {
  callback: CancelAndRefundCallbacks;
  subscription: CommunityMemberSubscription;
}

interface UsePaywallRefundSubscriptionApiReturn {
  fetchRefundOptionsSubscription: UseQueryResult<
    RefundOptionsResponse,
    unknown
  >;
  refundMutation: UseMutationResult<
    unknown,
    unknown,
    Record<string, unknown>,
    unknown
  >;
}

export const usePaywallRefundSubscriptionApi = ({
  subscription,
  callback,
}: UsePaywallRefundSubscriptionApiProps): UsePaywallRefundSubscriptionApiReturn => {
  const { success, error } = useToast();
  const defaultErrorMessage = t(
    "settings.paywalls_admin_dashboard.subscriptions.alerts.error.action_failed",
  );

  const queryClient = useQueryClient();
  const queryKeyString = `paywall-subscription-${subscription.processor_id}-refund-options`;
  const fetchRefundOptionsSubscription = useQuery<RefundOptionsResponse>({
    queryKey: queryKeyString,
    queryFn: () =>
      refundOptionsSubscription({
        subscriptionProcessorId: subscription.processor_id,
      }),
    refetchOnMount: false,
    onError: () => {
      const defaultValue: RefundOptionsResponse = {
        prorated_applicable: true,
        full_refund_applicable: true,
      };
      queryClient.setQueryData(queryKeyString, () => defaultValue);
    },
  });

  const refundMutation = useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      cancelAndRefundSubscription({
        subscriptionProcessorId: subscription.processor_id,
        params,
      }),
    onSuccess: () => {
      callback.cancelAndRefundSubscription.onSuccess();
      success(
        t(
          "settings.paywalls_admin_dashboard.subscriptions.alerts.success.updated",
        ),
      );
    },
    onError: (updateError: any) => {
      callback.cancelAndRefundSubscription.onError();
      console.error(updateError?.message);
      error(updateError?.message ?? defaultErrorMessage, {
        duration: "long",
      });
    },
  });

  return {
    refundMutation,
    fetchRefundOptionsSubscription,
  };
};
